import { IGatsbySvg } from "@bond-london/gatsby-transformer-extracted-svg";
import { IRichTextInformation } from "@bond-london/graphcms-rich-text";
import classNames from "classnames";
import React from "react";
import { RTF } from "../elements/RTF";
import { Separator } from "../elements/Separator";
import {
  calculateAnimationDelay,
  useEntryAnimation,
} from "../hooks/Visibility";
import { ArrowIcon } from "../icons/ArrowIcon";
import {
  HalfWidthClassName,
  HalfWidthTextClassName,
  MinSlideHeightClassName,
  SlidePaddingClassName,
} from "../styles";

export interface SummarySlideContent {
  title?: string | null;
  message?: IRichTextInformation | null;
  id: string;
}

export const SummarySlide: React.FC<{
  title?: string | null;
  message?: IRichTextInformation | null;
  onNavigate: (index: number) => void;
  shape?: IGatsbySvg | null;
  slides?: Array<SummarySlideContent>;
}> = ({ shape, title, message, slides, onNavigate }) => {
  const [sectionRef, animationMode] = useEntryAnimation();
  return (
    <section
      ref={sectionRef}
      className={classNames(
        "relative bg-hyacinth",
        MinSlideHeightClassName,
        SlidePaddingClassName,
      )}
      data-component="Summary Slide"
    >
      <div className="page-grid max-width relative">
        {shape && (
          <div
            className={classNames(
              animationMode,
              "animate-appear",
              "absolute flex items-center justify-end",
              "bottom-mobile-margin left-mobile-margin right-mobile-margin top-mobile-margin",
              "md:bottom-tablet-margin md:left-tablet-margin md:right-tablet-margin md:top-tablet-margin",
              "lg:bottom-desktop-margin lg:left-desktop-margin lg:right-desktop-margin lg:top-desktop-margin",
            )}
          >
            <img
              src={shape.encoded ?? shape.encodedUrl}
              className="h-full object-contain"
              alt=""
            />
          </div>
        )}
        {title && (
          <h2
            className={classNames(
              HalfWidthClassName,
              "md:row-span-1 md:row-start-1",
              "h2 text-cotton",
              animationMode,
              "animate-enter-from-bottom",
            )}
          >
            {title}
          </h2>
        )}
        {message && (
          <RTF
            content={message}
            className={classNames(
              HalfWidthTextClassName,
              "md:row-span-2 md:row-start-1",
              "text-cotton",
              "mt:20px md:mt-0",
              animationMode,
              "animate-enter-from-bottom",
            )}
            fixedParagraphClassName="p2"
          />
        )}
        <ul
          className={classNames(
            "col-span-4 col-start-2 md:col-span-7 md:col-start-2 lg:col-span-12 lg:col-start-3",
            "lg:row-span-1 lg:row-start-3",
            "pt-40px",
          )}
        >
          {slides?.map(({ title, message, id }, index) => {
            const delay = calculateAnimationDelay(index);

            return (
              <li
                key={id}
                className={classNames(
                  delay,
                  "animate-enter-from-bottom",
                  animationMode,
                )}
              >
                {index > 0 && <Separator />}
                <h3 className="h4 text-lazer">{title}</h3>
                <button
                  aria-label="Next"
                  className="float-right"
                  onClick={() => onNavigate(index + 1)}
                >
                  <ArrowIcon className="rotate-180 transform-gpu text-lazer" />
                </button>
                {message && (
                  <RTF
                    className="text-cotton"
                    fixedParagraphClassName="p2"
                    content={message}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
